import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import AOS from 'aos'
import Text from '../../../../../elements/Typo/Text'
import toast from 'react-hot-toast'
import { SuccessResponse, ErrorResponse } from '../../../../../store/api/apiTypes'
import { useKpiApi } from '../../../../../store/api/hooks/KpiApiHook'
import { Area, AreaChart, CartesianGrid, Legend, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import DataTableLoader from '../../../../../components/DataTableLoader'
import { getChartColor } from '../../../../../_config'
import moment from 'moment'
import { convertToKFormat, getDecimalPlacesByRange, trackEvent } from '../../../../../_common/functions'
import { CustomTooltip } from './AverageMetric'
import Icon from '../../../../../components/icons'
import { EXPERIMENT_TYPES } from '../../../../../_common/constant'

interface RespLineDateI {
    chartfor: string
    data: {
        title: string
        xAxis: {
            title: string
            categories: string[]
        }
        yAxis: {
            title: string
            max?: number
            min?: number
        }
        series: {
            name: string
            data: string[]
        }[]
    }
}

interface LegendFormatterProps {
    value: string
}

const LegendFormatter: React.FC<LegendFormatterProps> = ({ value }) => {
    return (
        <span className="text-[0.542rem] leading-[0.833rem] ml-1" style={{ color: 'black' }}>
            {value}
        </span>
    )
}

const createGroupData = (data: any, viewType: string) => {
    const groupedData: any = data.reduce((acc: any, item: any) => {
        const date = moment(item.name)
        let dateKey = `${date.clone().startOf('isoWeek').format('DD-MMM')} - ${date.clone().endOf('isoWeek').format('DD-MMM')}, ${date.year()}` // Group by week
        if (viewType == 'monthly') {
            dateKey = date.format('MMM YYYY')
        }
        const valueKeys = Object.keys(item).filter((key) => key !== 'name')
        if (!acc[dateKey]) {
            acc[dateKey] = { name: dateKey }

            for (const key of valueKeys) {
                acc[dateKey][key] = 0 // Initialize group values
            }
        }
        for (const key of valueKeys) {
            acc[dateKey][key] = Number((acc[dateKey][key] + (item[key] || 0)).toFixed(2)) // Sum up group values
        }
        return acc
    }, {})
    return Object.values(groupedData)
}

const MAX_TICKS = 500

const _handleXAxisValue = (dataset: any[], step: number) => {
    if (dataset.length) {
        const start = +dataset[0]['name']
        const stop = +dataset.at(-1)['name']
        if (Number.isNaN(start) || Number.isNaN(stop) || step <= 0) return []
        const count = Math.ceil((stop - start) / step)
        if (count <= 0 || count > MAX_TICKS) return []

        return Array.from({ length: count }, (_, i) => Number.parseInt('' + (start + i * step)))
    } else {
        return []
    }
}

const CustomReferenceLineLabel = ({ viewBox, label }: any) => {
    const { x, y } = viewBox
    return (
        <text x={x} y={y + 15} fill="black" fontSize={14} fontWeight="bold" textAnchor="middle">
            {label}
        </text>
    )
}

const useZoomFunctionality = (
    selectedKPI: string | null,
    plotlyData: any,
    setPlotlyData: any,
    originalData: any,
): { _handleMouseUp: () => void; zoomReset: () => void; _handleMouseDown: (e: any) => void; _zoomIn: () => void; _zoomOut: () => void; chartWidth: number } => {
    const [zoom, setZoom] = useState<number>(1)
    const [chartWidth, setChartWidth] = useState<number>(1)

    useEffect(() => {
        const rechartDiv = document.querySelector('.rechart-div')
        const baseWidth = rechartDiv ? rechartDiv.clientWidth : 970
        setChartWidth(baseWidth * zoom)
    }, [zoom])

    const _handleMouseUp = () => {
        if (selectedKPI && plotlyData[selectedKPI]?.data) {
            let { refAreaLeft, refAreaRight } = plotlyData[selectedKPI ?? '']
            if (refAreaLeft === refAreaRight || refAreaRight === '') {
                setPlotlyData({ [selectedKPI ?? '']: { ...plotlyData[selectedKPI ?? ''], refAreaLeft: '', refAreaRight: '' } })
                return
            }
            const indx = plotlyData?.[selectedKPI ?? '']?.data?.findIndex((i: any) => i.name === refAreaLeft)
            const indx2 = plotlyData?.[selectedKPI ?? '']?.data?.findIndex((i: any) => i.name === refAreaRight)

            if (indx > indx2) [refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft]
            setPlotlyData({ [selectedKPI ?? '']: { ...plotlyData[selectedKPI ?? ''], data: plotlyData[selectedKPI ?? ''].data.slice(indx, indx2 + 1), refAreaLeft: '', refAreaRight: '', left: indx, right: indx2 } })
        }
    }

    const zoomReset = () => {
        setPlotlyData(originalData.current)
        setZoom(1)
    }

    const _handleMouseDown = (e: any) => {
        if (e?.activeLabel) {
            setPlotlyData((prev: any) => ({
                ...prev,
                [selectedKPI || '']: {
                    ...prev[selectedKPI ?? ''],
                    refAreaLeft: e.activeLabel,
                },
            }))
        }
    }

    const _zoomIn = () => {
        setZoom((z) => Math.min(z * 1.5, 8))
    }

    const _zoomOut = () => {
        const rechartDiv = document.querySelector('.rechart-div')
        const baseWidth = rechartDiv ? rechartDiv.clientWidth : 970
        const newZoom = Math.max(1, Math.min(zoom / 1.5, 8))
        if (baseWidth * newZoom >= baseWidth) {
            setZoom(newZoom)
        }
    }

    return { _handleMouseUp, zoomReset, _handleMouseDown, _zoomIn, _zoomOut, chartWidth }
}

interface Props {
    experimentDetails: any
    selectedGroup: string | null
    selectedKPI: string | null
    mtegrouptext: string | null
    selectedDateRange: any[]
    selectedOptionConfig?: string | null
    toDownloadAsReport?: boolean
    reportError?: (x: boolean) => void
}
const AverageVolumePerConsumer: React.FC<Props> = ({ experimentDetails, selectedGroup, selectedKPI, mtegrouptext, selectedDateRange, selectedOptionConfig, toDownloadAsReport = false, reportError }) => {
    const { t } = useTranslation()
    const { getAvgNetVolumnPerPocPerDay } = useKpiApi()

    const originalData = useRef<{
        [key: string]: {
            data: any[]
            title: string
            xAxisTitle: string
            yAxisTitle: string
            yAxixRange: number[]
            refAreaLeft: string
            refAreaRight: string
            top: number
            left: number
            right: number
            bottom: number
        }
    }>({})

    const [plotlyData, setPlotlyData] = useState<{
        [key: string]: {
            data: any[]
            title: string
            xAxisTitle: string
            yAxisTitle: string
            yAxixRange: number[]

            refAreaLeft: string
            refAreaRight: string
            top: number
            left: number
            right: number
            bottom: number
        }
    }>({})
    const [chartLoading, setChartLoading] = useState<boolean>(false)
    const [decimalDigit, setDecimalDigit] = useState<number>(2)
    const [visibilityLines, setVisibilityLines] = useState<{
        [key: string]: boolean
    }>({})

    const [measurementDateRangeComputed, setMeasurementDateRangeComputed] = useState<boolean>(false)

    const [lineChartReferenceStartDate, setLineChartReferenceStartDate] = useState<any>(moment(experimentDetails?.start_date).format('MMM DD, YYYY'))

    const { _handleMouseUp: _rawHandleMouseUp, zoomReset, _handleMouseDown, _zoomIn, _zoomOut, chartWidth } = useZoomFunctionality(selectedKPI, plotlyData, setPlotlyData, originalData)

    const _handleMouseUp = () => {
        if (selectedKPI && plotlyData[selectedKPI]?.refAreaLeft && plotlyData[selectedKPI]?.refAreaRight && plotlyData[selectedKPI]?.refAreaLeft !== plotlyData[selectedKPI]?.refAreaRight) {
            trackEvent('experiment_details_chart_drag_zoom', { experiment_literal: experimentDetails?.experiment_literal, kpi_name: selectedKPI })
        }
        _rawHandleMouseUp()
    }

    const buildChartData = (kpi: any) => {
        console.log('buildChartData kpi', kpi)
        let data2 = kpi.data.xAxis.categories.map((category: string, index: number) => {
            const data3: Record<string, any> = { name: category }
            for (const series of kpi.data.series) {
                data3[series.name] = +series.data[index] || 0
            }
            return data3
        })

        if (selectedKPI == 'Total Distribution') {
            data2 = data2.map((item: any) => {
                if (item['Control'] !== undefined) {
                    const { Control, ...rest } = item
                    return { ...rest, Counterfactual: Control }
                }
                return item
            })
        }

        console.log('buildChartData data2', data2)

        return {
            data: data2,
            title: kpi.data?.title || '',
            xAxisTitle: kpi.data?.xAxis?.title || '',
            yAxisTitle: kpi.data?.yAxis?.title || '',
        }
    }

    const extractYAxisRange = (kpi: any) => {
        const yAxis = kpi.data?.yAxis || {}
        return ['min', 'max'].every((key) => key in yAxis) ? [yAxis.min, yAxis.max].map(Number) : []
    }

    const updateTitleAndReferenceDate = (chart: any, kpi: any) => {
        let date: any = moment(experimentDetails?.start_date)
        if (selectedDateRange.length === 2) {
            date = moment(selectedDateRange[0])
        }
        const weekStart = moment(experimentDetails?.experiment_metadatas?.measurement_column_map?.analysis_start_week || Date.now())

        const viewType = experimentDetails?.experiment_metadatas?.measurement_column_map?.date_frequency

        switch (viewType) {
            case 'weekly':
                chart.title = chart.title.replace('day', 'week')
                setLineChartReferenceStartDate(weekStart.format('MMM DD, YYYY'))
                //setLineChartReferenceStartDate(`${date.clone().startOf('isoWeek').format('DD-MMM')} - ${date.clone().endOf('isoWeek').format('DD-MMM')}, ${date.year()}`)
                break
            case 'monthly':
                chart.title = chart.title.replace('day', 'month')
                setLineChartReferenceStartDate(date.format('MMM YYYY'))
                break
            default:
                setLineChartReferenceStartDate(date.format('MMM DD, YYYY'))
        }
    }

    const groupDataIfNeeded = (data: Record<string, any>) => {
        const viewType = experimentDetails?.experiment_metadatas?.measurement_column_map?.date_frequency
        if (viewType !== 'monthly') return

        for (const key of Object.keys(data)) {
            const isDatePresent = !!data[key].data[0]?.name
            if (isDatePresent) {
                data[key].data = createGroupData(data[key].data, viewType)
            }
        }
    }

    const _handleFetchChartData = (group_name: string) => {
        setChartLoading(true)
        const params: any = {
            literal: experimentDetails.experiment_literal,
            group_name,
            methodology: selectedOptionConfig,
            kpiname: mtegrouptext ? 'Volume' : selectedKPI,
            mtegrouptext,
        }
        if (selectedDateRange.length == 2) {
            params.measurement_start_date = selectedDateRange[0]
            params.measurement_end_date = selectedDateRange[1]
            params.is_slice_and_dice = true
        } else {
            params.measurement_start_date = null
            params.measurement_end_date = null
            params.is_slice_and_dice = false
        }
        getAvgNetVolumnPerPocPerDay(
            params,
            (resp: SuccessResponse<RespLineDateI[]>) => {
                const result = resp.data
                const data: any = {}

                for (const kpi of result) {
                    const chartData = buildChartData(kpi)
                    const yAxixRange = extractYAxisRange(kpi)

                    data[kpi.chartfor] = {
                        ...chartData,
                        yAxixRange,
                        top: yAxixRange[1],
                        right: chartData.data.length,
                        left: 0,
                        bottom: yAxixRange[0],
                        refAreaRight: '',
                        refAreaLeft: '',
                    }

                    if (selectedKPI !== 'Total Distribution') {
                        updateTitleAndReferenceDate(data[kpi.chartfor], kpi)
                    }
                }

                originalData.current = data

                if (selectedKPI !== 'Total Distribution') {
                    groupDataIfNeeded(data)
                }

                console.log('PlotlyData', data)

                setPlotlyData(data)
                setChartLoading(false)
            },
            (error: ErrorResponse<any>) => {
                setChartLoading(false)
                if (reportError) {
                    reportError(true)
                }
                toast.error(error.message || t('Something went wrong'))
            },
        )
    }

    const toggleLines = (lineDetails: any) => {
        const { dataKey } = lineDetails

        // let updatedVisibilityLines = JSON.parse(JSON.stringify(visibilityLines))
        const cloneobj = (obj: any) => {
            if (typeof globalThis.structuredClone === 'function') {
                return globalThis.structuredClone(obj)
            }
            return { ...obj }
        }

        let updatedVisibilityLines: any = cloneobj(visibilityLines)

        let newVisible: boolean
        if (Object.keys(updatedVisibilityLines).includes(dataKey)) {
            updatedVisibilityLines[dataKey] = !updatedVisibilityLines[dataKey]
            newVisible = updatedVisibilityLines[dataKey]
        } else {
            updatedVisibilityLines = {
                ...updatedVisibilityLines,
                [dataKey]: false,
            }
            newVisible = false
        }

        trackEvent('experiment_details_chart_legend_toggled', { experiment_literal: experimentDetails?.experiment_literal, kpi_name: selectedKPI, series_key: dataKey, visible: newVisible })
        setVisibilityLines(updatedVisibilityLines)
    }

    useEffect(() => {
        if (selectedDateRange.length == 2 && selectedDateRange[0] && selectedDateRange[1]) {
            setMeasurementDateRangeComputed(true)
        }
        if (selectedDateRange.length == 0) {
            setMeasurementDateRangeComputed(false)
        }
    }, [selectedDateRange])

    useEffect(() => {
        if (experimentDetails?.experiment_literal && selectedGroup) {
            _handleFetchChartData(selectedGroup)
        }
    }, [experimentDetails, selectedGroup, selectedOptionConfig, selectedKPI, measurementDateRangeComputed])

    useEffect(() => {
        const params: any = {}

        if (toDownloadAsReport) {
            params['disable'] = true // Animation disabled
        } else {
            params['once'] = false // Animation occurs every time you scroll to the element
        }

        AOS.init(params)
        AOS.refresh()
    }, [])

    useEffect(() => {
        if (selectedKPI && originalData?.current?.[selectedKPI]?.['data']?.length) {
            const values: any[] = []
            const keys: any[] = Object.keys(originalData.current[selectedKPI]['data'][0])

            for (const gr of keys) {
                if (gr !== 'name') {
                    values.push(originalData.current[selectedKPI]['data'][0][gr])
                }
            }

            setDecimalDigit(getDecimalPlacesByRange(values))
        }
    }, [selectedKPI, originalData.current])

    const formatter = (value: any) => {
        return <LegendFormatter value={value} />
    }

    const _renderAreaChart = () => {
        return (
            <AreaChart
                width={500}
                height={400}
                data={(selectedKPI && plotlyData[selectedKPI]?.data) || []}
                margin={{
                    top: 0,
                    right: 30,
                    left: 0,
                    bottom: 0,
                }}
                onMouseDown={_handleMouseDown}
                onMouseUp={_handleMouseUp}
                onMouseMove={(e) => {
                    setPlotlyData((prev: any) => {
                        if (!prev[selectedKPI || '']?.refAreaLeft) return prev
                        return { ...prev, [selectedKPI || '']: { ...prev[selectedKPI || ''], refAreaRight: e?.activeLabel ?? '' } }
                    })
                }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="name"
                    // type="number"
                    ticks={_handleXAxisValue((selectedKPI && plotlyData[selectedKPI]?.data) || [], 1)}
                    label={{
                        value: (selectedKPI && plotlyData[selectedKPI]?.xAxisTitle) || '',
                        color: '#808080',
                        position: 'insideBottom',
                        offset: -15,
                        style: {
                            textAnchor: 'middle',
                            fill: '#808080',
                        },
                    }}
                />
                <YAxis
                    label={{
                        value: (selectedKPI && plotlyData[selectedKPI]?.yAxisTitle) || '', // Label text
                        angle: -90, // Rotate the label to vertical
                        position: 'insideLeft', // Position of the label
                        offset: 15,
                        color: '#808080',
                    }}
                    domain={selectedKPI && plotlyData[selectedKPI]?.yAxixRange?.length ? plotlyData[selectedKPI].yAxixRange : ['auto', 'auto']}
                    width={60}
                    axisLine={false}
                    allowDataOverflow={true}
                />
                <Tooltip content={<CustomTooltip decimalDigit={decimalDigit} />} cursor={{ fill: 'transparent' }} />
                <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                        paddingTop: 20,
                        left: 35,
                    }}
                    iconType="circle"
                    iconSize={9}
                    formatter={formatter}
                    onClick={(e) => {
                        toggleLines(e.payload)
                    }}
                />

                {selectedKPI &&
                    plotlyData[selectedKPI]?.data?.[0] &&
                    Object.keys(plotlyData[selectedKPI]?.data[0])
                        .filter((key) => key !== 'name')
                        .map((key: string, index: number) => {
                            return (
                                <Area
                                    key={`${key}-${index}`}
                                    type="monotone"
                                    dataKey={key}
                                    stroke={getChartColor(key, index)}
                                    fill={getChartColor(key, index)}
                                    fillOpacity={0.3}
                                    hide={Object.keys(visibilityLines).includes(key) && visibilityLines[key] === false}
                                    isAnimationActive={!toDownloadAsReport}
                                />
                            )
                        })}

                {plotlyData[selectedKPI || '']?.refAreaLeft && plotlyData[selectedKPI || '']?.refAreaRight ? (
                    <ReferenceArea x1={plotlyData[selectedKPI || '']?.refAreaLeft} x2={plotlyData[selectedKPI || '']?.refAreaRight} strokeOpacity={0.3} />
                ) : null}
            </AreaChart>
        )
    }

    const _renderLineChart = () => {
        return (
            <LineChart
                data={selectedKPI && plotlyData[selectedKPI]?.data ? plotlyData[selectedKPI]?.data : []}
                margin={{
                    top: 0,
                    right: 30,
                    left: 20,
                    bottom: 0,
                }}
                onMouseDown={_handleMouseDown}
                onMouseUp={_handleMouseUp}
                onMouseMove={(e) => {
                    setPlotlyData((prev: any) => {
                        if (!prev[selectedKPI || '']?.refAreaLeft) return prev
                        return { ...prev, [selectedKPI || '']: { ...prev[selectedKPI || ''], refAreaRight: e?.activeLabel || '' } }
                    })
                }}
            >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                    allowDataOverflow={true}
                    domain={[plotlyData[selectedKPI || '']?.left, plotlyData[selectedKPI || '']?.right]}
                    dataKey="name"
                    tick={true}
                    tickLine={false}
                    label={{
                        value: (selectedKPI && plotlyData[selectedKPI]?.xAxisTitle) || '',
                        color: '#808080',
                        position: 'insideBottom',
                        offset: -15,
                        style: {
                            textAnchor: 'middle',
                            fill: '#808080',
                        },
                    }}
                />
                <YAxis
                    label={{
                        value: (selectedKPI && plotlyData[selectedKPI]?.yAxisTitle) || '', // Label text
                        angle: -90, // Rotate the label to vertical
                        position: 'insideLeft', // Position of the label
                        offset: 0,
                        color: '#808080',
                    }}
                    width={60}
                    axisLine={false}
                    allowDataOverflow={true}
                    tickFormatter={(value: any) => convertToKFormat(value, decimalDigit)}
                />
                <Tooltip content={<CustomTooltip decimalDigit={decimalDigit} />} cursor={{ fill: 'transparent' }} />
                <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    wrapperStyle={{ paddingTop: 20, left: 35 }}
                    align="center"
                    iconType="circle"
                    iconSize={9}
                    formatter={formatter}
                    onClick={(e) => {
                        toggleLines(e.payload)
                    }}
                />
                <ReferenceLine x={lineChartReferenceStartDate} stroke="black" strokeDasharray="3 3" label={<CustomReferenceLineLabel label={t('Measurement Begins')} />} />
                {selectedKPI &&
                    Object.keys(plotlyData[selectedKPI]?.data?.[0] ?? {})
                        .filter((key) => key !== 'name')
                        .map((key: string, index: number) => (
                            <Line
                                key={`${key}-${index}`}
                                type="monotone"
                                dataKey={key}
                                stroke={getChartColor(key, index)}
                                activeDot={{ r: 8 }}
                                dot={false}
                                hide={Object.keys(visibilityLines).includes(key) && visibilityLines[key] === false}
                                isAnimationActive={!toDownloadAsReport}
                            />
                        ))}

                {plotlyData[selectedKPI || '']?.refAreaLeft && plotlyData[selectedKPI || '']?.refAreaRight ? (
                    <ReferenceArea x1={plotlyData[selectedKPI || '']?.refAreaLeft} x2={plotlyData[selectedKPI || '']?.refAreaRight} strokeOpacity={0.3} />
                ) : null}
            </LineChart>
        )
    }

    const _renderChart = () => {
        if (selectedKPI === 'Total Distribution') {
            return _renderAreaChart()
        } else if (selectedKPI && plotlyData[selectedKPI]?.data) {
            return _renderLineChart()
        } else {
            return (
                <div className="flex items-center justify-center h-full w-full bg-gray-50">
                    <Text>{t('No Data Found')}</Text>
                </div>
            )
        }
    }

    return (
        <div
            className={
                experimentDetails?.experiment_type?.['title'] == EXPERIMENT_TYPES.MTE.title
                    ? 'col-span-3 md:col-span-2 md:row-span-1 md:col-start-2 md:row-start-1 bg-base-100 rounded-[0.45rem] shadow-1'
                    : 'col-span-3 md:col-span-2 md:row-span-2 md:col-start-2 md:row-start-1 bg-base-100 rounded-[0.45rem] shadow-1'
            }
            data-aos="zoom-in"
            data-aos-delay="100"
        >
            {chartLoading ? (
                <DataTableLoader className="h-6 w-6" />
            ) : (
                <div className="px-3 roushared with:nded-[0.7rem]">
                    <div className="py-3">
                        <div className="flex  flex-col md:flex-row justify-between items-stretch pr-2  mb-0 gap-2">
                            <Text size="md" className="font-semibold">
                                {(selectedKPI && plotlyData[selectedKPI]?.title) || ''}
                            </Text>
                            <div className="mt-1 shadow-1 rounded-md flex">
                                <button
                                    type="button"
                                    className="bg-white text-center hover:bg-[#F5F5F5] border-r px-[0.8rem] rounded-l"
                                    onClick={() => {
                                        trackEvent('experiment_details_chart_zoom_out', { experiment_literal: experimentDetails?.experiment_literal, kpi_name: selectedKPI })
                                        _zoomOut()
                                    }}
                                    title={t('Zoom Out')}
                                >
                                    <span className="font-normal text-nowrap">-</span>
                                </button>
                                <button
                                    type="button"
                                    className="bg-white text-center hover:bg-[#F5F5F5] border-r px-[0.8rem]"
                                    title={t('Zoom In')}
                                    onClick={() => {
                                        trackEvent('experiment_details_chart_zoom_in', { experiment_literal: experimentDetails?.experiment_literal, kpi_name: selectedKPI })
                                        _zoomIn()
                                    }}
                                >
                                    <span className="font-normal text-nowrap">+</span>
                                </button>
                                <button
                                    type="button"
                                    className="hover:bg-[#F5F5F5] px-[0.8rem] rounded-r items-center flex justify-center"
                                    onClick={() => {
                                        trackEvent('experiment_details_chart_zoom_reset', { experiment_literal: experimentDetails?.experiment_literal, kpi_name: selectedKPI })
                                        zoomReset()
                                    }}
                                    title={t('Reset Zoom')}
                                >
                                    <Icon name="reset" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="w-full h-full text-[0.5rem] flex relative select-none rechart-div">
                        <div style={{ width: '100%', overflowX: 'auto' }}>
                            <div style={{ width: chartWidth }}></div>
                            <ResponsiveContainer width={chartWidth} height={experimentDetails?.experiment_type?.title === EXPERIMENT_TYPES.RCT.title ? 580 : 500} key={chartWidth}>
                                {_renderChart()}
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
export default AverageVolumePerConsumer
